import React, { useState, useRef } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import CanvasField from "../../components/CanvasField";
import FieldSelector from "../../components/FieldSelector";

const FIELD_TYPES = [
  { type: "text", label: "Text Field" },
  { type: "textbox", label: "Textarea" },
  { type: "dropdown", label: "Dropdown" },
  { type: "checkbox", label: "Checkbox" },
  { type: "radio", label: "Radio Button" },
  { type: "email", label: "Email" },
  { type: "tel", label: "Telephone" },
  { type: "url", label: "URL" },
  { type: "search", label: "Search" },
  { type: "range", label: "Range" },
  { type: "number", label: "Number" },
  { type: "datetime-local", label: "Date & Time" },
  { type: "date", label: "Date" },
  { type: "time", label: "Time" },
  { type: "month", label: "Month" },
  { type: "week", label: "Week" },
  { type: "file", label: "File Upload" },
  { type: "image", label: "Image Upload" },
];

const PreviewField = ({ field, index, movePreviewField, removePreviewField }) => {
  const ref = useRef(null);
  const [, drag] = useDrag({ type: "PREVIEW_FIELD", item: { index } });
  const [, drop] = useDrop({
    accept: "PREVIEW_FIELD",
    hover: (item) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      movePreviewField(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  drag(drop(ref));

  const renderOptions = () => {
    if (!field.options) return null;
    const opts = field.options.split(",");
    if (field.type === "dropdown") {
      return (
        <select className="border p-1 w-full" required>
          <option value="" disabled selected>
            Select an option
          </option>
          {opts.map((opt, idx) => (
            <option key={idx} value={opt.trim()}>
              {opt.trim()}
            </option>
          ))}
        </select>
      );
    }
    if (field.type === "checkbox" || field.type === "radio") {
      return (
        <div className="flex flex-wrap gap-2">
          {opts.map((opt, idx) => (
            <div
              key={idx}
              className="bg-blue-200 text-blue-800 px-2 py-1 rounded flex items-center"
            >
              {field.type === "checkbox" ? (
                <input type="checkbox" className="mr-1" />
              ) : (
                <input type="radio" name={field.fieldName} className="mr-1" required />
              )}
              {opt.trim()}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div ref={ref} className="p-3 mb-2 border rounded bg-gray-50 shadow-sm relative">
      <label className="block font-medium mb-1">
        {field.fieldLabel} {field.mandatory && <span className="text-red-500">*</span>}
      </label>

      {field.type === "text" && (
        <input type="text" className="border p-1 w-full" min={field.min} max={field.max} required />
      )}
      {field.type === "textbox" && (
        <textarea className="border p-1 w-full" minLength={field.min} maxLength={field.max} required />
      )}
      {field.type === "number" && (
        <input type="number" className="border p-1 w-full" min={field.min} max={field.max} required />
      )}
      {field.type === "range" && (
        <input type="range" className="border w-full" min={field.min} max={field.max} required />
      )}
      {field.type === "email" && <input type="email" className="border p-1 w-full" required />}
      {field.type === "tel" && <input type="tel" className="border p-1 w-full" required />}
      {field.type === "url" && <input type="url" className="border p-1 w-full" required />}
      {field.type === "search" && <input type="search" className="border p-1 w-full" required />}
      {field.type === "file" && <input type="file" className="border p-1 w-full" required />}
      {field.type === "image" && <input type="file" accept="image/*" className="border p-1 w-full" required />}
      {(field.type === "dropdown" || field.type === "checkbox" || field.type === "radio") && renderOptions()}

      <button
        onClick={() => removePreviewField(field.tempId)}
        className="absolute top-1 right-1 text-red-500 font-bold"
      >
        ×
      </button>
    </div>
  );
};

const HeadCheckList = () => {
  const [selectedType, setSelectedType] = useState("");
  const [canvasFields, setCanvasFields] = useState([]);
  const [previewFields, setPreviewFields] = useState([]);

  const addField = () => {
    if (!selectedType) return;
    if (canvasFields.length > 0) {
      alert("Canvas has a input, please save it or remove it before adding another");
      return;
    }
    const fieldDef = FIELD_TYPES.find((f) => f.type === selectedType);
    const newField = {
      tempId: Date.now(),
      type: fieldDef.type,
      fieldName: "",
      fieldLabel: "",
      mandatory: false,
      min: "",
      max: "",
      options:
        fieldDef.type === "dropdown" ||
        fieldDef.type === "checkbox" ||
        fieldDef.type === "radio"
          ? ""
          : undefined,
    };
    setCanvasFields([newField]);
  };

  const saveField = (tempId) => {
    const fieldToSave = canvasFields.find((f) => f.tempId === tempId);
    if (!fieldToSave) return;

    if (
      !fieldToSave.fieldName.trim() ||
      !fieldToSave.fieldLabel.trim() ||
      ((fieldToSave.type === "text" ||
        fieldToSave.type === "textbox" ||
        fieldToSave.type === "number" ||
        fieldToSave.type === "range") &&
        (!fieldToSave.min || !fieldToSave.max))
    ) {
      alert("Please fill Field Name, Field Label, Min, and Max before saving.");
      return;
    }

    setPreviewFields((prev) => [...prev, fieldToSave]);
    setCanvasFields([]);
  };

  const movePreviewField = (fromIndex, toIndex) => {
    const updated = [...previewFields];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setPreviewFields(updated);
  };

  const removePreviewField = (tempId) => {
    setPreviewFields((prev) => prev.filter((f) => f.tempId !== tempId));
  };

  const saveAll = () => {
    console.log("Fields to save:", previewFields);
    alert("Saved to DB! (check console log)");
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="p-6 max-w-4xl mx-auto">
        <h2 className="text-3xl font-extrabold mb-6 text-blue-800 text-center">
          Head Check List Builder
        </h2>

        <FieldSelector
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          addField={addField}
          fieldTypes={FIELD_TYPES}
        />

        {/* Canvas Section */}
        <div className="mb-6">
          {canvasFields.length === 0 ? (
            <div className="w-full min-h-[200px] border-2 border-dashed border-gray-400 p-4 rounded">
              <h3 className="font-bold mb-2">Form Canvas</h3>
              <p className="text-gray-500">Select a field to add</p>
            </div>
          ) : (
            <CanvasField
              field={canvasFields[0]}
              updateField={(key, value) =>
                setCanvasFields([{ ...canvasFields[0], [key]: value }])
              }
              saveField={saveField}
              removeField={() => setCanvasFields([])}
            />
          )}
        </div>

        {/* Preview Section */}
        <div className="mb-6 border p-4 rounded bg-gray-100">
          <h3 className="font-bold mb-2">Preview</h3>
          {previewFields.length === 0 ? (
            <p className="text-gray-500">No fields saved yet</p>
          ) : (
            previewFields.map((f, index) => (
              <PreviewField
                key={f.tempId}
                field={f}
                index={index}
                movePreviewField={movePreviewField}
                removePreviewField={removePreviewField}
              />
            ))
          )}

          {previewFields.length > 0 && (
            <button
              onClick={saveAll}
              className="mt-3 bg-green-600 text-white px-4 py-2 rounded"
            >
              Save All
            </button>
          )}
        </div>
      </div>
    </DndProvider>
  );
};

export default HeadCheckList;
